import * as React from 'react';

import { useQuery } from '@apollo/client';
import type { Campaign_ProductType as ProductType } from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import CampaignModulesDataContext from 'bundles/campaigns/context/CampaignModulesDataContext';
import useIsPreview from 'bundles/campaigns/hooks/useIsPreview';
import GetCampaignModulesForXdpQuery from 'bundles/campaigns/queries/GetCampaignModulesForXdpQuery.graphql';
import type {
  GetCampaignModulesForXdpQuery as GetCampaignModulesForXdpQueryType,
  GetCampaignModulesForXdpQueryVariables,
} from 'bundles/campaigns/queries/__generated__/GetCampaignModulesForXdpQuery';
import { SentryErrorType, captureExceptionInSentry } from 'bundles/campaigns/utils/sentryUtil';
import shouldShowCampaigns from 'bundles/campaigns/utils/shouldShowCampaigns';

const CampaignModulesDataProviderForXdp = ({
  children,
  productType,
}: React.PropsWithChildren<{ productType: ProductType }>) => {
  const { query } = useLocation();
  const { productSlug } = useParams();
  const isPreview = useIsPreview();
  const campaignId = query.campaignId ?? null;

  const { data, loading } = useQuery<GetCampaignModulesForXdpQueryType, GetCampaignModulesForXdpQueryVariables>(
    GetCampaignModulesForXdpQuery,
    {
      context: {
        clientName: 'gatewayGql',
      },
      variables: {
        campaignModulesVariables: {
          useContentfulPreview: isPreview,
          forceUseCampaignId: campaignId,
        },
        productType,
        slug: productSlug,
      },
      errorPolicy: 'all',
      ssr: true,
      skip: !shouldShowCampaigns(),
      onError: (error) => {
        captureExceptionInSentry({
          error,
          extra: {
            message: 'Error fetching campaign modules on XDP',
            campaignId,
            isPreview,
            productType,
            productSlug,
          },
          errorType: SentryErrorType.GRAPHQL_ERROR,
        });
      },
    }
  );

  let promoBarModuleData;

  const modules = data?.Campaign?.getCampaignModulesForXdp;
  if (modules?.promoBar?.__typename === 'Campaign_PromoBarModule') {
    promoBarModuleData = modules.promoBar;
  }

  return (
    <CampaignModulesDataContext.Provider
      value={{
        loading,
        campaignData: {
          promoBarModule: promoBarModuleData,
        },
      }}
    >
      {children}
    </CampaignModulesDataContext.Provider>
  );
};

export default CampaignModulesDataProviderForXdp;
